import React, { useState } from 'react';
import { FolderPlus, Plus, Check } from 'lucide-react';

// UI Components
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

// Internal imports
import { useLists } from '../hooks/use-lists';
import { useAddRestaurantToList, useBulkAddRestaurantToLists } from '../hooks/use-restaurant-lists';
import { getListTypeInfo } from '../constants/list-types';
import { CreateListModal } from './create-list-modal';
import { Restaurant } from '@/features/dashboard/pages/restaurants/types/restaurants.types';

interface AddToListModalProps {
  restaurant: Restaurant;
  trigger?: React.ReactNode;
}

export const AddToListModal: React.FC<AddToListModalProps> = ({ restaurant, trigger }) => {
  const [open, setOpen] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);

  const { data: listsResponse, isLoading: isLoadingLists } = useLists();
  const addRestaurantToList = useAddRestaurantToList();
  const bulkAddRestaurantToLists = useBulkAddRestaurantToLists();

  const lists = listsResponse?.data || [];

  const handleListToggle = (listId: string) => {
    setSelectedListIds(prev =>
      prev.includes(listId)
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleAddToLists = async () => {
    if (selectedListIds.length === 0) {
      return;
    }

    try {
      if (selectedListIds.length === 1) {
        await addRestaurantToList.mutateAsync({
          restaurant,
          listId: selectedListIds[0]!,
        });
      } else {
        await bulkAddRestaurantToLists.mutateAsync({
          restaurant,
          listIds: selectedListIds,
        });
      }

      // Reset state and close modal
      setSelectedListIds([]);
      setOpen(false);
    } catch (error) {
      // Error handling is done in the hooks
      console.error('Failed to add restaurant to lists:', error);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset selected lists when closing
      setSelectedListIds([]);
    }
  };

  const isLoading = addRestaurantToList.isPending || bulkAddRestaurantToLists.isPending;

  const defaultTrigger = (
    <Button size="sm" variant="outline" className="flex items-center gap-1">
      <FolderPlus className="h-4 w-4" />
      Add to List
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add to Lists</DialogTitle>
          <DialogDescription>
            Add "{restaurant.name}" to your lists. You can select multiple lists.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoadingLists ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3 animate-pulse">
                  <div className="w-4 h-4 bg-muted rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2 mt-1"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : lists.length === 0 ? (
            <div className="text-center py-8">
              <FolderPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Lists Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first list to start organizing your restaurants.
              </p>
              <CreateListModal
                trigger={
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First List
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {lists.map((list) => {
                  const listTypeInfo = getListTypeInfo(list.listType);
                  const isSelected = selectedListIds.includes(list.id);

                  return (
                    <div
                      key={list.id}
                      className="flex items-center space-x-3 p-3 rounded-sm border hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleListToggle(list.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleListToggle(list.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{list.listName}</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${listTypeInfo.color}`}
                          >
                            {listTypeInfo.label}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Created {new Date(list.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t pt-4">
                <CreateListModal
                  trigger={
                    <Button variant="outline" className="w-full">
                      <Plus className="mr-2 h-4 w-4" />
                      Create New List
                    </Button>
                  }
                />
              </div>
            </>
          )}
        </div>

        {lists.length > 0 && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToLists}
              disabled={selectedListIds.length === 0 || isLoading}
            >
              {isLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Adding...
                </>
              ) : (
                <>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Add to {selectedListIds.length} List{selectedListIds.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};